import React, { useState, useEffect, useRef } from 'react';

interface OptimizedImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  loading?: 'lazy' | 'eager';
  priority?: boolean;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  objectPosition?: string;
  sizes?: string;
}

/**
 * OptimizedImage component for better SEO and Core Web Vitals
 * Provides proper error handling, aspect ratio preservation, and accessibility
 */
const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  width,
  height,
  className = '',
  loading = 'lazy',
  priority = false,
  objectFit = 'cover',
  objectPosition = 'center',
  sizes
}) => {
  const [imageSrc, setImageSrc] = useState(src);
  const [imageError, setImageError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const previousSrc = useRef(src);

  // Generate descriptive alt text if not provided
  const descriptiveAlt = alt || 'BBHA Insurance Services - Medicare specialist';

  // Create privacy-safe image URLs without tracking
  const createPrivacyUrl = (originalSrc: string) => {
    return originalSrc;
  };

  // Create responsive srcSet for better performance
  const createSrcSet = (originalSrc: string) => {
    const privacyUrl = createPrivacyUrl(originalSrc);
    
    if (originalSrc.includes('pexels.com')) {
      const baseUrl = privacyUrl.split('?')[0];
      return [
        `${baseUrl}?w=400&h=320&fit=crop&crop=face,smart 400w`,
        `${baseUrl}?w=800&h=640&fit=crop&crop=face,smart 800w`,
        `${baseUrl}?w=1200&h=960&fit=crop&crop=face,smart 1200w`
      ].join(', ');
    }
    
    return undefined;
  };

  const handleError = () => {
    setImageError(true);
    // Fallback to a default placeholder image
    setImageSrc('/bhba-logo.png');
  };

  const handleLoad = () => {
    setIsLoaded(true);
  };

  useEffect(() => {
    // Do not reset the initial load state here. A cached image can finish before
    // this effect runs, and resetting it would leave the image hidden forever
    // because the unchanged URL does not emit a second load event.
    if (previousSrc.current === src) return;

    previousSrc.current = src;
    setImageSrc(createPrivacyUrl(src));
    setImageError(false);
    setIsLoaded(false);
  }, [src]);

  const imageStyle: React.CSSProperties = {
    objectFit,
    objectPosition,
    transition: 'opacity 0.1s ease-out',
    opacity: isLoaded ? 1 : 0
  };

  // Add aspect ratio preservation when width and height are provided
  if (width && height) {
    imageStyle.aspectRatio = `${width} / ${height}`;
  }

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {/* Loading placeholder */}
      {!isLoaded && (
        <div 
          className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center"
          style={{ aspectRatio: width && height ? `${width} / ${height}` : undefined }}
        >
          <div className="text-gray-400 text-sm">Loading...</div>
        </div>
      )}

      <img
        decoding="async"
        src={imageSrc}
        alt={descriptiveAlt}
        width={width}
        height={height}
        loading={priority ? 'eager' : loading}
        srcSet={createSrcSet(src)}
        sizes={sizes || '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw'}
        onError={handleError}
        onLoad={handleLoad}
        style={imageStyle}
        className={`w-full h-full ${objectFit === 'cover' ? 'object-cover' : `object-${objectFit}`}`}
        // Privacy and security attributes
        crossOrigin={src.includes('images.pexels.com') ? 'anonymous' : undefined}
        referrerPolicy="no-referrer-when-downgrade"
        // Add structured data for images
        itemProp="image"
        // Accessibility improvements
        role="img"
        aria-describedby={imageError ? 'image-error-fallback' : undefined}
      />

      {/* Error state indicator (hidden but accessible) */}
      {imageError && (
        <span id="image-error-fallback" className="sr-only">
          Image failed to load, showing company logo instead
        </span>
      )}
    </div>
  );
};

export default OptimizedImage;
